import { useState } from "react";
import * as React from "react";
import {
  Button,
  FormGroup,
  Intent,
  Classes,
  Dialog,
  InputGroup,
  Callout
} from "@blueprintjs/core";
import FormInfo from "../../../atoms/FormInfo/FormInfo";
import DefaultToaster from "../../../atoms/DefaultToaster";
import { useMutation } from "@apollo/react-hooks";
import { UpdateAccountPassword } from "./mutation";
import Auth0 from "../../../helpers/Auth0";

function PasswordInput({ profile }) {
  const [passwordStatus, setPasswordStatus] = useState(0);
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [error, setError] = useState();
  const [updatePassword] = useMutation(UpdateAccountPassword);

  function closeDialog() {
    setIsDialogOpen(false);
    setPasswordStatus(0);
    setError(undefined);
  }

  async function updatePasswordClick() {
    if (newPassword === confirmPassword) {
      await updatePassword({
        variables: {
          password: newPassword,
          confirmPassword: confirmPassword
        }
      });

      setIsDialogOpen(false);

      DefaultToaster.show({
        message: "Your password has been changed!",
        icon: "tick",
        intent: Intent.SUCCESS
      });
    } else {
      setError({
        message: "Password doesn't match."
      });
    }
  }

  async function confirmPasswordClick() {
    const auth0 = new Auth0();
    try {
      await auth0.login(profile.email, password);
      setError(undefined);
      setPasswordStatus(1);
    } catch (err) {
      setError({
        title: "Invalid Password.",
        message: "The password you entered is incorrect."
      });
    }
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (passwordStatus === 0) confirmPasswordClick();
    else if (passwordStatus === 1) updatePasswordClick();
  }

  return (
    <>
      <Dialog
        icon="lock"
        onClose={closeDialog}
        title="Account Password Required"
        isOpen={isDialogOpen}
      >
        <form onSubmit={handleSubmit}>
          <div className={Classes.DIALOG_BODY}>
            {error && (
              <Callout
                title={error.message}
                intent={Intent.DANGER}
                icon="error"
              />
            )}

            {passwordStatus === 0 && (
              <FormGroup label="Current Password" labelFor="currentPassword">
                <InputGroup
                  id="currentPassword"
                  type="password"
                  autoCapitalize="none"
                  autoCorrect="false"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </FormGroup>
            )}

            {passwordStatus === 1 && (
              <>
                <FormInfo>
                  Please create a password that is at least 8 characters long.
                </FormInfo>
                <FormGroup label="New Password">
                  <InputGroup
                    type="password"
                    placeholder="New Password"
                    autoCapitalize="none"
                    autoCorrect="false"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                  />
                </FormGroup>

                <FormGroup label="Confirm Password">
                  <InputGroup
                    type="password"
                    placeholder="Confirm Password"
                    autoCapitalize="none"
                    autoCorrect="false"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                </FormGroup>
              </>
            )}
            <FormInfo>
              For account protection, your current Video Squirrel password is
              required to save changes to your settings.
            </FormInfo>
          </div>

          <div className={Classes.DIALOG_FOOTER}>
            <div className={Classes.DIALOG_FOOTER_ACTIONS}>
              <Button onClick={closeDialog} type="button" minimal>
                Cancel
              </Button>
              {passwordStatus === 0 && (
                <Button type="submit" intent={Intent.PRIMARY}>
                  Next
                </Button>
              )}
              {passwordStatus === 1 && (
                <Button type="submit" intent={Intent.PRIMARY}>
                  Confirm
                </Button>
              )}
            </div>
          </div>
        </form>
      </Dialog>

      <FormGroup label="Password">
        <FormInfo>
          A strong password contains a mix of numbers, letters, and symbols. It
          is hard to guess, does not resemble a real word, and is only used for
          this account.
        </FormInfo>
        <Button
          text="Update Password"
          intent={Intent.PRIMARY}
          onClick={() => setIsDialogOpen(true)}
        />
      </FormGroup>
    </>
  );
}

export default PasswordInput;
